import { useState, useEffect, useRef } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { twoFactorLoginApi } from '../services/api'
import {
  ShieldCheck,
  Smartphone,
  Mail,
  Key,
  Fingerprint,
  Loader2,
  AlertCircle,
  ArrowLeft,
  RefreshCw,
  CheckCircle
} from 'lucide-react'

export default function TwoFactorVerifyPage() {
  const navigate = useNavigate()
  const { twoFactorPending, complete2FALogin, cancel2FA, isAuthenticated } = useAuth()
  
  // Stati
  const [method, setMethod] = useState('') // Sarà impostato in base ai metodi disponibili
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [emailSent, setEmailSent] = useState(false)
  const [emailCooldown, setEmailCooldown] = useState(0)
  const [webAuthnSupported, setWebAuthnSupported] = useState(false)
  
  const inputRef = useRef(null)

  // Check WebAuthn support
  useEffect(() => {
    setWebAuthnSupported(
      typeof window !== 'undefined' && 
      window.PublicKeyCredential !== undefined
    )
  }, [])

  // Imposta metodo iniziale in base a quelli disponibili
  useEffect(() => {
    if (twoFactorPending?.methods) {
      const m = twoFactorPending.methods
      // Priorità: totp > email > webauthn > backup
      if (m.totp) setMethod('totp')
      else if (m.email) setMethod('email')
      else if (m.webauthn && webAuthnSupported) setMethod('webauthn')
      else setMethod('backup')
    }
  }, [twoFactorPending, webAuthnSupported])

  // Focus input on method change
  useEffect(() => {
    if (inputRef.current && method !== 'webauthn') {
      inputRef.current.focus()
    }
  }, [method])

  // Email cooldown timer
  useEffect(() => {
    if (emailCooldown > 0) {
      const timer = setTimeout(() => setEmailCooldown(c => c - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [emailCooldown])

  // Redirect se già autenticato
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  // Redirect se non c'è 2FA pending
  if (!twoFactorPending) {
    return <Navigate to="/login" replace />
  }

  // Estrai dati dal pending state
  // methods è un oggetto: { totp: bool, email: bool, webauthn: bool }
  const { twoFactorToken, methods } = twoFactorPending
  
  // Metodi disponibili
  const hasTotp = methods?.totp === true
  const hasEmail = methods?.email === true
  const hasWebAuthn = methods?.webauthn === true && webAuthnSupported

  // Verifica codice (TOTP, Email OTP, Backup)
  const handleVerify = async (e) => {
    e?.preventDefault()
    
    if (!code || code.length < 6) {
      setError('Inserisci un codice valido')
      return
    }

    setLoading(true)
    setError('')

    try {
      const response = await twoFactorLoginApi.verify(
        twoFactorToken,
        code,
        method === 'backup' ? 'backup' : method,
        false // rememberDevice - superadmin non usa trusted devices
      )

      const data = response.data.data

      if (data.accessToken) {
        // Login completato con successo
        await complete2FALogin(data.accessToken, data.refreshToken, data.user)
        navigate('/dashboard')
      } else {
        setError('Verifica fallita')
      }
    } catch (err) {
      console.error('2FA verify error:', err)
      const message = err.response?.data?.message || 'Codice non valido'
      setError(message)
      setCode('')
    } finally {
      setLoading(false)
    }
  }

  // Invia codice email
  const handleSendEmailCode = async () => {
    if (emailCooldown > 0) return

    setLoading(true)
    setError('')

    try {
      await twoFactorLoginApi.sendEmailCode(twoFactorToken)
      setEmailSent(true)
      setEmailCooldown(60) // 60 secondi cooldown
      setMethod('email')
    } catch (err) {
      console.error('Send email code error:', err)
      setError(err.response?.data?.message || 'Errore nell\'invio del codice')
    } finally {
      setLoading(false)
    }
  }

  // Verifica con Passkey/WebAuthn
  const handlePasskeyVerify = async () => {
    if (!webAuthnSupported) {
      setError('Il tuo browser non supporta le Passkey')
      return
    }

    setLoading(true)
    setError('')

    try {
      // 1. Ottieni opzioni dal server
      const optionsRes = await twoFactorLoginApi.getWebAuthnOptions(twoFactorToken)
      const options = optionsRes.data.data

      // 2. Converti base64 in ArrayBuffer
      options.challenge = base64ToArrayBuffer(options.challenge)
      if (options.allowCredentials) {
        options.allowCredentials = options.allowCredentials.map(cred => ({
          ...cred,
          id: base64ToArrayBuffer(cred.id)
        }))
      }

      // 3. Richiedi autenticazione al browser
      const credential = await navigator.credentials.get({
        publicKey: options
      })

      // 4. Prepara risposta per il server
      const webauthnResponse = {
        id: credential.id,
        rawId: arrayBufferToBase64(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: arrayBufferToBase64(credential.response.clientDataJSON),
          authenticatorData: arrayBufferToBase64(credential.response.authenticatorData),
          signature: arrayBufferToBase64(credential.response.signature),
          userHandle: credential.response.userHandle 
            ? arrayBufferToBase64(credential.response.userHandle) 
            : null
        }
      }

      // 5. Verifica sul server
      const verifyRes = await twoFactorLoginApi.verifyWebAuthn(
        twoFactorToken, 
        webauthnResponse,
        false // rememberDevice
      )

      const data = verifyRes.data.data

      if (data.accessToken) {
        await complete2FALogin(data.accessToken, data.refreshToken, data.user)
        navigate('/dashboard')
      } else {
        setError('Verifica passkey fallita')
      }
    } catch (err) {
      console.error('Passkey verify error:', err)
      if (err.name === 'NotAllowedError') {
        setError('Autenticazione annullata o timeout')
      } else {
        setError(err.response?.data?.message || 'Errore nella verifica passkey')
      }
    } finally {
      setLoading(false)
    }
  }

  // Torna al login
  const handleBack = () => {
    cancel2FA()
    navigate('/login')
  }

  // Helper WebAuthn
  const base64ToArrayBuffer = (base64) => {
    const binaryString = atob(base64.replace(/-/g, '+').replace(/_/g, '/'))
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    return bytes.buffer
  }

  const arrayBufferToBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer)
    let binary = ''
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i])
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-primary py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
              <ShieldCheck className="h-8 w-8 text-primary-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">Verifica in due passaggi</h1>
            <p className="mt-2 text-sm text-gray-600">
              Verifica la tua identità per continuare
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Method Tabs */}
          <div className="flex gap-2 mb-6 p-1 bg-gray-100 rounded-lg">
            {hasTotp && (
              <button
                onClick={() => { setMethod('totp'); setError(''); setCode(''); }}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  method === 'totp'
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Smartphone className="h-4 w-4" />
                <span className="hidden sm:inline">App</span>
              </button>
            )}
            {hasEmail && (
              <button
                onClick={() => { setMethod('email'); setError(''); setCode(''); }}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  method === 'email'
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Mail className="h-4 w-4" />
                <span className="hidden sm:inline">Email</span>
              </button>
            )}
            {hasWebAuthn && (
              <button
                onClick={() => { setMethod('webauthn'); setError(''); }}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  method === 'webauthn'
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Fingerprint className="h-4 w-4" />
                <span className="hidden sm:inline">Passkey</span>
              </button>
            )}
            {/* Backup codes sempre disponibile come fallback */}
            <button
              onClick={() => { setMethod('backup'); setError(''); setCode(''); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                method === 'backup'
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Key className="h-4 w-4" />
              <span className="hidden sm:inline">Backup</span>
            </button>
          </div>

          {/* TOTP Method */}
          {method === 'totp' && (
            <form onSubmit={handleVerify} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Codice da Google Authenticator
                </label>
                <input
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  maxLength={6}
                />
              </div>
              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full flex items-center justify-center px-4 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Verifica...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Verifica
                  </>
                )}
              </button>
            </form>
          )}

          {/* Email Method */}
          {method === 'email' && (
            <div className="space-y-4">
              {!emailSent ? (
                <>
                  <p className="text-sm text-gray-600 text-center">
                    Riceverai un codice all'indirizzo email associato al tuo account.
                  </p>
                  <button
                    onClick={handleSendEmailCode}
                    disabled={loading || emailCooldown > 0}
                    className="w-full flex items-center justify-center px-4 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Invio...
                      </>
                    ) : emailCooldown > 0 ? (
                      <>
                        <RefreshCw className="h-5 w-5 mr-2" />
                        Riprova tra {emailCooldown}s
                      </>
                    ) : (
                      <>
                        <Mail className="h-5 w-5 mr-2" />
                        Invia codice via email
                      </>
                    )}
                  </button>
                </>
              ) : (
                <form onSubmit={handleVerify} className="space-y-4">
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-700 flex items-center">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Codice inviato! Controlla la tua email.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Codice ricevuto via email
                    </label>
                    <input
                      ref={inputRef}
                      type="text"
                      inputMode="numeric"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      maxLength={6}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading || code.length !== 6}
                    className="w-full flex items-center justify-center px-4 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Verifica...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-5 w-5 mr-2" />
                        Verifica
                      </>
                    )}
                  </button>
                  {emailCooldown === 0 && (
                    <button
                      type="button"
                      onClick={handleSendEmailCode}
                      disabled={loading}
                      className="w-full text-sm text-primary-600 hover:text-primary-700"
                    >
                      Non hai ricevuto il codice? Invia di nuovo
                    </button>
                  )}
                </form>
              )}
            </div>
          )}

          {/* WebAuthn/Passkey Method */}
          {method === 'webauthn' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 text-center">
                Usa Face ID, Touch ID, Windows Hello o la tua chiave di sicurezza per verificare la tua identità.
              </p>
              <button
                onClick={handlePasskeyVerify}
                disabled={loading}
                className="w-full flex items-center justify-center px-4 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Verifica in corso...
                  </>
                ) : (
                  <>
                    <Fingerprint className="h-5 w-5 mr-2" />
                    Usa Passkey
                  </>
                )}
              </button>
            </div>
          )}

          {/* Backup Code Method */}
          {method === 'backup' && (
            <form onSubmit={handleVerify} className="space-y-4">
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-700">
                  I codici di backup sono monouso. Usa questo metodo solo se non hai accesso agli altri.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Codice di backup
                </label>
                <input
                  ref={inputRef}
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 10))}
                  placeholder="XXXXXXXXXX"
                  className="w-full px-4 py-3 text-center text-xl font-mono tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 uppercase"
                  maxLength={10}
                />
              </div>
              <button
                type="submit"
                disabled={loading || code.length < 8}
                className="w-full flex items-center justify-center px-4 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Verifica...
                  </>
                ) : (
                  <>
                    <Key className="h-5 w-5 mr-2" />
                    Usa codice backup
                  </>
                )}
              </button>
            </form>
          )}

          {/* Back button */}
          <div className="mt-6 pt-6 border-t border-gray-100">
            <button
              onClick={handleBack}
              className="w-full flex items-center justify-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Torna al login
            </button>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-sm text-white/70">
          © {new Date().getFullYear()} Il Diario di Bordo
        </p>
      </div>
    </div>
  )
}
